import { useState } from 'react';
import { DialogContent, DialogActions, Box, TextField, Typography, Button, CircularProgress, Divider } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { toast } from 'react-toastify';
import { useSendBat } from '../../../../../../hooks/Marketing';
import { replaceAnchors } from './personalAnchors';
import { RIGHTS, full, useRights } from '../../../../../../rights';

interface BatPreviewFormProps {
  channel: 'EMAIL' | 'SMS';
  html: string;
  initialSubject: string;
  usedAnchors: string[];
  onSent: () => void;
}

function BatPreviewForm({ channel, html, initialSubject, usedAnchors, onSent }: BatPreviewFormProps) {
  const { sendBat, loading } = useSendBat();
  const isSms = channel === 'SMS';
  const { can } = useRights();
  // BeOne : un BAT SMS ne part vers plusieurs numéros qu'avec da.sms.bat_multiples
  const singleSmsRecipient = isSms && !can(full(RIGHTS.sms.multipleBat));
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState(initialSubject);
  const [sender, setSender] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const handleSend = async () => {
    const recipientList = recipients.split(';').map(recipient => recipient.trim()).filter(Boolean);
    if (recipientList.length === 0) {
      toast.warning('Veuillez renseigner au moins un destinataire.');
      return;
    }
    if (singleSmsRecipient && recipientList.length > 1) {
      toast.warning("Vous n'avez le droit d'envoyer un BAT SMS qu'à un seul numéro.");
      return;
    }
    // batservice accepte un BAT email sans objet (READY), mais il n'arrive pas : on l'exige avant l'envoi
    if (!isSms && !subject.trim()) {
      toast.warning("Veuillez renseigner l'objet de l'email.");
      return;
    }
    const outcome = await sendBat({
      channel,
      message: replaceAnchors(html, fieldValues, usedAnchors),
      recipients: recipientList,
      object: isSms ? undefined : subject.trim(),
      sender: sender || undefined,
      fields: usedAnchors.map(anchor => ({ name: anchor, value: fieldValues[anchor] ?? '' })),
    });
    if (outcome.ok) {
      toast.success(outcome.message || 'BAT envoyé avec succès.');
      onSent();
    } else {
      toast.error(outcome.message);
    }
  };

  return (
    <>
      <DialogContent sx={{ px: 3, '&.MuiDialogContent-root': { paddingTop: '20px' } }}>
        <TextField
          label={isSms ? 'Numéros de mobile' : 'Destinataires'}
          value={recipients}
          onChange={e => setRecipients(e.target.value)}
          placeholder={isSms ? '0612345678' : 'prenom.nom@exemple.fr'}
          helperText={singleSmsRecipient ? "Un seul numéro de mobile." : "Séparez les différents destinataires par un point-virgule(;)."}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {!isSms && <TextField required label="Objet" value={subject} onChange={e => setSubject(e.target.value)} fullWidth size="small" />}
          <TextField label="Expéditeur" value={sender} onChange={e => setSender(e.target.value)} fullWidth size="small" />
        </Box>

        {usedAnchors.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
              Champ de personnalisation :
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {usedAnchors.map(anchor => (
                <TextField
                  key={anchor}
                  label={anchor}
                  value={fieldValues[anchor] ?? ''}
                  onChange={e => setFieldValues(prev => ({ ...prev, [anchor]: e.target.value }))}
                  fullWidth
                  size="small"
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'grey.200', px: 3, py: 1.5 }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
          onClick={handleSend}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.main', opacity: 0.9 } }}
        >
          Envoyer un BAT
        </Button>
      </DialogActions>
    </>
  );
}

export default BatPreviewForm;
